import React, { useState, useEffect, useContext } from 'react';
import { Table, Button } from "reactstrap";
import ViaticosPlanillaService from "../axios_services/viaticos_planilla.service";
import Loader from "../components/Loader/Loader";
import { ButtonUpdate, ButtonDelete, ButtonInsert, ButtonSearch } from '../components/Buttons/Buttons';
import ModalPlanillaViatico from '../components/Modal/ModalPlanillaViatico';
import { FileText } from 'lucide-react';
import { AppContext } from '../application/provider';
import ArchivosService from '../axios_services/archivos.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './CajaFinancieroMovimiento.css'; // Reutilizando estilos

const getRowStyle = (validacionId) => {
    const e = Number(validacionId);
    if (e === 4) return { backgroundColor: '#dcfce7' };
    if (e === 5 || e === 6) return { backgroundColor: '#fef2f2' };
    return { backgroundColor: '#dbeafe' }; // Pendiente Subgerencia
};

function PlanillaViaticos() {
    const [planillas, setPlanillas] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPlanilla, setSelectedPlanilla] = useState(null);
    const [state] = useContext(AppContext);

    const now = new Date();
    const mesActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const [periodoInput, setPeriodoInput] = useState(mesActual);
    const [periodoFiltro, setPeriodoFiltro] = useState(mesActual);

    const perfilId = Number(state.user?.perfil_id);
    const userId = Number(state.user?.usuario_id);
    const esAdministrativo = [8, 26].includes(perfilId);

    const formatoSoles = (monto) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
        }).format(monto || 0);
    };

    const getCargo = (uId) => {
        const personalList = state.user?.personal || [];
        const perfilesList = state.catalogos?.perfiles || [];
        const persona = personalList.find(p => Number(p.usuario_id) === Number(uId));
        if (persona) {
            const perfil = perfilesList.find(pf => Number(pf.perfil_id) === Number(persona.perfil_id));
            return perfil ? (perfil.descripcion || perfil.perfil_desc || 'NO DEFINIDO').toUpperCase() : 'NO DEFINIDO';
        }
        return 'NO DEFINIDO';
    };

    useEffect(() => {
        fetchPlanillas();
    }, []);

    const handleSearch = async () => {
        setPeriodoFiltro(periodoInput);
        await fetchPlanillas();
    };

    const fetchPlanillas = async () => {
        setIsLoading(true);
        try {
            const response = await ViaticosPlanillaService.getPlanillas();
            setPlanillas(response.data || []);
        } catch (error) {
            console.error('Error al obtener planillas:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNuevaPlanilla = () => {
        setSelectedPlanilla(null);
        setIsModalOpen(true);
    };

    const handleEditarPlanilla = (planilla) => {
        setSelectedPlanilla(planilla);
        setIsModalOpen(true);
    };

    const handleEliminarPlanilla = async (planillaId) => {
        if (!window.confirm('¿Está seguro que desea eliminar este registro?')) {
            return;
        }
        try {
            setIsLoading(true);
            await ViaticosPlanillaService.deletePlanilla(planillaId);
            await fetchPlanillas();
        } catch (error) {
            console.error('Error al eliminar planilla:', error);
            alert('Error al eliminar el registro');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPlanilla(null);
    };

    const handleSavePlanilla = async (planillaData) => {
        try {
            setIsLoading(true);
            setIsModalOpen(false);
            if (selectedPlanilla) {
                await ViaticosPlanillaService.updatePlanilla({ ...planillaData, planilla_id: selectedPlanilla.planilla_id });
            } else {
                await ViaticosPlanillaService.insertPlanilla(planillaData);
            }
            await fetchPlanillas();
        } catch (error) {
            console.error('Error al guardar planilla:', error);
            alert(error.response?.data?.error || 'Ocurrió un error al guardar.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleValidarEmpleado = async (planilla_id) => {
        if (!window.confirm('¿Confirma que ha recibido el monto de esta planilla de viáticos?')) {
            return;
        }
        try {
            setIsLoading(true);
            await ViaticosPlanillaService.validarPlanillaEmpleado(planilla_id);
            alert('Recepción confirmada correctamente.');
            await fetchPlanillas();
        } catch (error) {
            console.error('Error al validar planilla:', error);
            alert(error.response?.data?.error || 'Ocurrió un error al validar.');
        } finally {
            setIsLoading(false);
        }
    };

    const loadImg = (url) => new Promise(async (res) => {
        if (!url) return res(null);
        try { const r = await fetch(url); const b = await r.blob(); const rd = new FileReader(); rd.onloadend = () => res(rd.result); rd.onerror = () => res(null); rd.readAsDataURL(b); } catch { res(null); }
    });

    const handleVerPlanillaPDF = async (planilla) => {
        setIsLoading(true);
        try {
            // Fetch firmas
            const [firmaTesRes, firmaColabRes] = await Promise.all([
                planilla.tesoreria_id ? ArchivosService.getFirmaDigitalConvenio(planilla.tesoreria_id).catch(() => null) : null,
                planilla.usuario_id ? ArchivosService.getFirmaDigitalConvenio(planilla.usuario_id).catch(() => null) : null
            ]);

            const doc = new jsPDF('p', 'mm', 'a4');

            const toTitleCase = (str) => {
                if (!str) return '';
                const exceptions = ['de', 'del', 'la', 'las', 'el', 'los', 'en', 'y', 'a', 'por', 'con', 'para'];
                return str.toLowerCase().split(' ').map((word, index) => {
                    if (index > 0 && exceptions.includes(word)) {
                        return word;
                    }
                    return word.charAt(0).toUpperCase() + word.slice(1);
                }).join(' ');
            };

            // Header Styles (Logo and Lines)
            doc.setDrawColor(29, 52, 97);
            doc.setLineWidth(0.8);
            doc.line(15, 15, 195, 15);
            try {
                const l = await loadImg(`${window.location.origin}/logo_mec.png`);
                if (l) doc.addImage(l, undefined, 15, 18, 18, 18);
            } catch { }

            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "bold");
            // El numero de la planilla
            const numPlanilla = planilla.numero_planilla || '___';
            doc.text(`PLANILLA DE VIÁTICOS N° ${numPlanilla}`, 45, 30);

            doc.setDrawColor(221, 227, 240);
            doc.setLineWidth(0.2);
            doc.line(15, 40, 195, 40);

            doc.setFontSize(10);

            let startY = 50;
            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "bold");
            doc.text("PARA:", 20, startY);
            doc.setFont("helvetica", "normal");
            doc.text(".................................................................................................", 40, startY);
            doc.text(toTitleCase(planilla.nombre_empleado || ''), 45, startY - 1);

            startY += 8;
            doc.setFont("helvetica", "bold");
            doc.text("CARGO:", 20, startY);
            doc.setFont("helvetica", "normal");
            doc.text(".....................................................", 40, startY);
            doc.text(toTitleCase(getCargo(planilla.usuario_id)), 45, startY - 1);

            startY += 8;
            doc.setFont("helvetica", "bold");
            doc.text("FECHA:", 20, startY);
            doc.setFont("helvetica", "normal");
            doc.text(".....................................................", 40, startY);
            doc.text(planilla.fecha_registro || '', 45, startY - 1);

            startY += 10;
            const textLines = doc.splitTextToSize(`Por el presente documento, se hace entrega de los viáticos correspondientes para cumplir con la labor asignada en la ciudad de ${toTitleCase(planilla.lugar)}, desde el ${planilla.dia_inicio} hasta el ${planilla.dia_fin}. Dichos fondos se otorgan con la finalidad de cubrir los gastos de Transporte Interprovincial, Movilidad Local y Alimentación durante la comisión de servicio.\n\nLos viáticos entregados corresponden detalladamente a los siguientes conceptos:`, 170);
            doc.text(textLines, 20, startY);

            startY += (textLines.length * 5) + 5;

            const total = (Number(planilla.monto_pasaje) || 0) +
                (Number(planilla.monto_hospedaje) || 0) +
                (Number(planilla.monto_movilidad) || 0) +
                (Number(planilla.monto_alimentacion) || 0) +
                (Number(planilla.monto_adicional) || 0);

            autoTable(doc, {
                startY: startY,
                head: [['CONCEPTO', 'MONTO']],
                body: [
                    ['Transporte Interprovincial (Ida y vuelta)', formatoSoles(planilla.monto_pasaje)],
                    ['Hospedaje', formatoSoles(planilla.monto_hospedaje)],
                    ['Movilidad Local', formatoSoles(planilla.monto_movilidad)],
                    ['Alimentación', formatoSoles(planilla.monto_alimentacion)],
                    ['Adicional', formatoSoles(planilla.monto_adicional)],
                    [{ content: 'TOTAL', styles: { fontStyle: 'bold' } }, { content: formatoSoles(total), styles: { fontStyle: 'bold', fillColor: [255, 255, 0], textColor: [0, 0, 0], halign: 'right' } }]
                ],
                theme: 'grid',
                styles: { fontSize: 10, cellPadding: 3, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2 },
                headStyles: { fillColor: [237, 125, 49], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
                columnStyles: {
                    0: { halign: 'left', cellWidth: 80 },
                    1: { halign: 'right', cellWidth: 40 }
                },
                margin: { left: 40 }
            });

            const finalY = doc.lastAutoTable.finalY;
            startY = finalY + 15;

            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "bold");
            const notaLines = doc.splitTextToSize("NOTA: La Rendición de viáticos es a los 03 días hábil de haber realizado el viaje.\nDe no rendir en el plazo indicado, este monto se descontará en el sueldo del mes correspondiente.", 170);
            doc.text(notaLines, 20, startY);

            startY += 40;

            const drawSignature = (imgData, label, subLabel, x) => {
                if (imgData) {
                    try { doc.addImage(imgData, undefined, x - 20, startY - 22, 40, 18); } catch { }
                }
                doc.setDrawColor(0); doc.setLineWidth(0.5); doc.line(x - 30, startY, x + 30, startY);
                doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
                doc.text(label || '', x, startY + 5, { align: 'center' });
                doc.setFont('helvetica', 'normal');
                doc.text(subLabel, x, startY + 10, { align: 'center' });
            };

            const pageWidth = doc.internal.pageSize.width;
            drawSignature(firmaTesRes?.data?.base64 || firmaTesRes?.base64, toTitleCase(planilla.nombre_tesoreria), "Responsable de Tesorería", pageWidth * 0.30);
            drawSignature(firmaColabRes?.data?.base64 || firmaColabRes?.base64, toTitleCase(planilla.nombre_empleado), "Colaborador", pageWidth * 0.70);

            // Open PDF in new tab
            window.open(URL.createObjectURL(doc.output("blob")));
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Error al generar el documento");
        } finally {
            setIsLoading(false);
        }
    };

    const planillasFiltradas = planillas.filter(p => p.fecha_registro && p.fecha_registro.startsWith(periodoFiltro));

    return (
        <div className="caja-flujo-container">
            {isLoading && <Loader />}
            <div className="flex items-center justify-between mb-4">
                <h1>PLANILLA DE VIÁTICOS</h1>
            </div>

            <div className="filtros" style={{ marginBottom: '10px' }}>
                <div>
                    <label>Mes:</label>
                    <input type="month" value={periodoInput} onChange={e => setPeriodoInput(e.target.value)} />
                </div>
            </div>

            <div className="mb-4">
                <ButtonSearch onClick={handleSearch} isLoading={isLoading} />
            </div>

            {esAdministrativo && (
                <div className="nuevo-movimiento-section mb-3">
                    <ButtonInsert onClick={handleNuevaPlanilla} />
                </div>
            )}

            <div className="table-container shadow-sm overflow-x-auto">
                <Table bordered hover size="sm" className="text-xs m-0">
                    <thead className="bg-[#14233a] text-white text-center align-middle">
                        <tr>
                            <th>COLABORADOR</th>
                            <th>ASUNTO</th>
                            <th>LUGAR</th>
                            <th style={{ minWidth: '110px' }}>FECHA INICIO</th>
                            <th style={{ minWidth: '110px' }}>FECHA FIN</th>
                            <th>MONTO ASIGNADO</th>
                            <th style={{ width: '140px' }}>ESTADO</th>
                            {esAdministrativo && <th>DOCUMENTO</th>}
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody className="align-middle">
                        {planillasFiltradas.length > 0 ? (
                            planillasFiltradas.map((planilla) => {
                                const total = (Number(planilla.monto_pasaje) || 0) +
                                    (Number(planilla.monto_hospedaje) || 0) +
                                    (Number(planilla.monto_movilidad) || 0) +
                                    (Number(planilla.monto_alimentacion) || 0) +
                                    (Number(planilla.monto_adicional) || 0);

                                let estadoTexto = "PENDIENTE SUBGERENCIA";
                                let badgeColor = "bg-secondary text-white";
                                let validacionId = Number(planilla.solicitud_validacion_id);

                                if (validacionId === 4) {
                                    estadoTexto = "APROBADO POR SUBGERENCIA";
                                    badgeColor = "bg-success text-white";
                                } else if (validacionId === 5 || validacionId === 6) {
                                    estadoTexto = "RECHAZADO";
                                    badgeColor = "bg-danger text-white";
                                }

                                return (
                                    <tr key={planilla.planilla_id} style={getRowStyle(validacionId)}>
                                        <td className="p-2 font-bold text-uppercase">{planilla.nombre_empleado}</td>
                                        <td className="p-2 text-center" style={{ maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={planilla.asunto || planilla.detalle_solicitud || '—'}>
                                            {planilla.asunto ? planilla.asunto.toUpperCase() : (planilla.detalle_solicitud ? planilla.detalle_solicitud.toUpperCase() : '—')}
                                        </td>
                                        <td className="p-2 text-center">{planilla.lugar}</td>
                                        <td className="p-2 text-center">{planilla.dia_inicio}</td>
                                        <td className="p-2 text-center">{planilla.dia_fin}</td>
                                        <td className="p-2 text-center text-primary" style={{ fontWeight: 'normal' }}>
                                            {formatoSoles(total)}
                                        </td>
                                        <td className="p-2 text-center align-middle" style={{ fontWeight: 'normal' }}>
                                            {estadoTexto}
                                        </td>
                                        {esAdministrativo && (
                                            <td className="p-2 text-center align-middle">
                                                <div className="flex justify-center">
                                                    <button
                                                        className="boton-accion"
                                                        title={validacionId === 4 ? "Ver PDF" : "Aún no validado por Gerencia"}
                                                        onClick={() => validacionId === 4 ? handleVerPlanillaPDF(planilla) : null}
                                                        style={{ cursor: validacionId === 4 ? 'pointer' : 'not-allowed', opacity: validacionId === 4 ? 1 : 0.6 }}
                                                        disabled={validacionId !== 4}
                                                    >
                                                        <FileText color={validacionId === 4 ? "#16a34a" : "#dc2626"} size={22} />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                        <td className="p-2 text-center align-middle">
                                            <div className="flex justify-center items-center gap-2">
                                                {esAdministrativo && validacionId !== 4 ? (
                                                    <>
                                                        <ButtonUpdate onClick={() => handleEditarPlanilla(planilla)} />
                                                        <ButtonDelete onClick={() => handleEliminarPlanilla(planilla.planilla_id)} />
                                                    </>
                                                ) : (
                                                    <span className="text-2xl" title="Bloqueado" style={{ cursor: 'not-allowed', display: 'flex', alignItems: 'center' }}>
                                                        🔒
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center text-gray-500 py-4">
                                    No hay datos disponibles.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalPlanillaViatico
                onSave={handleSavePlanilla}
                onClose={handleCloseModal}
                isOpen={isModalOpen}
                planilla={selectedPlanilla}
                periodoFiltro={periodoFiltro}
            />
        </div>
    );
}

export default PlanillaViaticos;
